import styled, { css } from "styled-components/macro";
import { NavLink, useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ERROR_COLOR, THEME_COLOR, THEME_COLOR_DARK } from "../../vars";
import {
  AccountCircle,
  PersonAdd,
  Pencil,
  SkipioLogo,
  Messages,
  Contacts,
  Calendar,
  MegaPhone,
  QuestionMarkCircle,
  People,
  ResourceCenter,
  Franchise,
  UpgradeArrow,
  HamburgerMenu,
} from "../Icons";
import Overlay from "../Overlay";
import { Menu, MenuButtonItem, MenuLabel } from "../Menu";
import MessageComposerSidePanel from "../MessageComposerSidePanel";
import { NewContactSidePanel } from "../ContactEditor";
import usePlan from "../../hooks/use-plan";
import usePaywall from "../../hooks/use-paywall";
import HelpButton from "../HelpButton";
import TooltipRight from "../TooltipRight";
import { transparentize } from "polished";
import useMoment from "../../hooks/use-moment";
import { ProfileOverlay } from "./ProfileOverlay";
import useWindowSize from "../../hooks/use-screen-size";
import useMessageNotifications from "../../hooks/use-message-notifications";
import useMilestoneNotifications from "../../hooks/use-milestone-notifications";
import { fetchBadgeCounts, fetchUser } from "../../api/users";
import { fetchBilling } from "../../api/billing";

const BRIGHT_THEME_COLOR = "#F9CC19";

export default function SideNav() {
  const { width } = useWindowSize();

  useMessageNotifications();
  useMilestoneNotifications();

  if (width >= 800) return <NavDesktop />;
  else return <NavMobile />;
}

function NavDesktop() {
  const history = useHistory();
  const moment = useMoment();

  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;

  const plan = usePlan();
  const showIntegrationsLink = Object.entries(plan.features).some(
    ([key, on]) => on && key.startsWith("integrations__")
  );
  const { percentOfMessagesUsed: percent } = plan;

  const hasBadge = plan.isAccountOwner && percent >= 50;
  const isRed = percent >= 90;

  const { unreadCount } = useQuery({
    queryKey: ["users", "me", "badgeCounts"],
    queryFn: fetchBadgeCounts,
    select: (queryData) => queryData.data,
  }).data!;

  const showPaywall = usePaywall();

  const hasTeamsDashboard =
    plan.features.teams &&
    (plan.isMultiUserOwner || user.teams.some((t) => t.isLeader));

  const { subscription } = useQuery({
    queryKey: ["billing"],
    queryFn: fetchBilling,
    select: (queryData) => queryData.data,
    suspense: false,
  }).data || { subscription: null };

  const isTrial = subscription && subscription.status === "trialing";

  return (
    <Container>
      <div>
        <NavbarLink to="/app/resource-center" activeClassName="active">
          <Logo>
            <SkipioLogo style={{ color: BRIGHT_THEME_COLOR }} />
          </Logo>
        </NavbarLink>
      </div>
      <Links>
        <MessageComposerSidePanel
          trigger={({ show, ref }) => (
            <TooltipRight label="New Message">
              <NavbarLink
                to="#"
                activeClassName="noop"
                onClick={show}
                ref={ref}
                accent
              >
                <Pencil />
              </NavbarLink>
            </TooltipRight>
          )}
        />
        {hasTeamsDashboard && (
          <TooltipRight label="My Team">
            <NavbarLink to="/app/admin/team" activeClassName="active">
              <People />
            </NavbarLink>
          </TooltipRight>
        )}
        {user.agencyOwner && (
          <TooltipRight label="Franchise Dashboard">
            <NavbarLink
              to="/app/admin/franchise-dashboard"
              activeClassName="active"
            >
              <Franchise />
            </NavbarLink>
          </TooltipRight>
        )}
        <TooltipRight label="Messages">
          <NavbarLink
            to="/app/messages"
            activeClassName="active"
            data-badge={unreadCount || undefined}
          >
            <Messages />
          </NavbarLink>
        </TooltipRight>
        <TooltipRight label="Contacts">
          <NavbarLink to="/app/contacts" activeClassName="active">
            <Contacts />
          </NavbarLink>
        </TooltipRight>
        <TooltipRight label="Calendar">
          <NavbarLink to="/app/calendar" activeClassName="active">
            <Calendar />
          </NavbarLink>
        </TooltipRight>
        <TooltipRight label="Campaigns">
          <NavbarLink to="/app/campaigns" activeClassName="active">
            <MegaPhone />
          </NavbarLink>
        </TooltipRight>
        <TooltipRight label="Resource Center">
          <NavbarLink to="/app/resource-center" activeClassName="active">
            <ResourceCenter />
          </NavbarLink>
        </TooltipRight>
        {plan.price !== null && Number(plan.price) === 0 && user.accountOwner && (
          <TooltipRight label="Upgrade">
            <NavbarLink to="/app/plan" lesserAccent>
              <UpgradeArrow />
            </NavbarLink>
          </TooltipRight>
        )}
        {isTrial && (
          <TooltipRight
            label={`${moment().to(
              moment(subscription.trialEnd),
              true
            )} remaining in your trial`}
          >
            <NavbarLink to="/app/plan" lesserAccent>
              <UpgradeArrow />
            </NavbarLink>
          </TooltipRight>
        )}
      </Links>

      <Profile>
        <HelpButton
          trigger={({ ref, show }) => (
            <TooltipRight label="Help">
              <NavbarLink
                to="#"
                activeClassName="noop"
                onClick={show}
                ref={ref}
              >
                <QuestionMarkCircle />
              </NavbarLink>
            </TooltipRight>
          )}
        />

        <Overlay
          border
          trigger={({ show, ref }) => (
            <TooltipRight label="New Contact">
              <NavbarLink
                to="#"
                activeClassName="noop"
                onClick={show}
                ref={ref}
              >
                <PersonAdd />
              </NavbarLink>
            </TooltipRight>
          )}
          position={{
            bottom: () => 8,
            left: ({ width }) => width,
          }}
          content={({ hide }) => (
            <div
              style={{
                width: "285px",
              }}
            >
              <Menu onClick={() => hide()}>
                <MenuLabel>Add New Contact</MenuLabel>
                {/* @ts-expect-error */}
                <NewContactSidePanel
                  // @ts-expect-error
                  trigger={({ ref, show }) => (
                    <MenuButtonItem
                      ref={ref}
                      onClick={
                        !plan.canUploadContacts
                          ? () => {
                              showPaywall(
                                "Looks like you're ready to add another contact. Great! Let's upgrade your account."
                              );
                            }
                          : show
                      }
                    >
                      New Contact
                    </MenuButtonItem>
                  )}
                />

                <MenuButtonItem
                  onClick={() => {
                    hide();
                    history.push("/app/csv-import");
                  }}
                >
                  Upload Contacts
                </MenuButtonItem>

                {showIntegrationsLink ? (
                  <MenuButtonItem
                    onClick={() => {
                      hide();
                      history.push("/app/integrations");
                    }}
                  >
                    View Integrations
                  </MenuButtonItem>
                ) : (
                  <MenuButtonItem
                    onClick={() => {
                      hide();
                      history.push(
                        "/app/success-center/post/4aabe149-ae1e-497b-b06a-7c48d53d270f"
                      );
                    }}
                  >
                    View Integrations
                  </MenuButtonItem>
                )}
              </Menu>
            </div>
          )}
        />

        <ProfileOverlay
          trigger={({ show, ref }) => (
            <TooltipRight label="Profile & Settings">
              <ProfilePicture onClick={show} ref={ref}>
                {user.avatarImageUrl ? (
                  <div
                    style={{ backgroundImage: `url(${user.avatarImageUrl})` }}
                  />
                ) : (
                  <ProfileIcon />
                )}
                {hasBadge && isRed && <ProfileBadge>!</ProfileBadge>}
              </ProfilePicture>
            </TooltipRight>
          )}
        />
      </Profile>
    </Container>
  );
}

function NavMobile() {
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const plan = usePlan();

  const { unreadCount } = useQuery({
    queryKey: ["users", "me", "badgeCounts"],
    queryFn: fetchBadgeCounts,
    select: (queryData) => queryData.data,
  }).data!;

  const { subscription } = useQuery({
    queryKey: ["billing"],
    queryFn: fetchBilling,
    select: (queryData) => queryData.data,
    suspense: false,
  }).data || { subscription: null };

  const isTrial = subscription && subscription.status === "trialing";

  return (
    <ContainerMobile>
      <NavbarLinkMobile
        to="/app/messages"
        activeClassName="active"
        data-badge={unreadCount || undefined}
      >
        <Messages />
      </NavbarLinkMobile>

      <NavbarLinkMobile to="/app/contacts" activeClassName="active">
        <Contacts />
      </NavbarLinkMobile>

      <NavbarLinkMobile to="/app/calendar" activeClassName="active">
        <Calendar />
      </NavbarLinkMobile>

      <NavbarLinkMobile to="/app/campaigns" activeClassName="active">
        <MegaPhone />
      </NavbarLinkMobile>

      {((plan.price !== null &&
        Number(plan.price) === 0 &&
        user.accountOwner) ||
        isTrial) && (
        <NavbarLinkMobile to="/app/plan">
          <UpgradeArrow />
        </NavbarLinkMobile>
      )}

      <ProfileOverlay
        trigger={({ show, ref }) => (
          <NavbarLinkMobile
            to="#"
            activeClassName="noop"
            ref={ref}
            onClick={show}
          >
            <HamburgerMenu />
          </NavbarLinkMobile>
        )}
      />
    </ContainerMobile>
  );
}

export const Container = styled.nav`
  grid-area: nav;
  width: 65px;
  flex: 0;
  display: flex;
  flex-direction: column;
  font-size: 0.9em;
  white-space: nowrap;
  background: #0a344a;
  position: relative;
  z-index: 1;
  @media (max-width: 800px) {
    display: none;
  }
`;

export const ContainerMobile = styled.div`
  height: 50px;
  background: #0a344a;
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
`;

export const Logo = styled.div`
  display: inline-flex;
  justify-items: center;
  align-items: center;
  width: 100%;
  height: 40px;
  ${SkipioLogo} {
    width: 28px;
    height: 28px;
  }
`;

export const Links = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const NavbarLink = styled(NavLink)<{
  accent?: boolean;
  lesserAccent?: boolean;
}>`
  display: inline-block;
  color: #9598a6;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  padding: 10px 15px;
  transition-duration: 0.2s;
  letter-spacing: 0.5px;
  font-weight: 500;
  text-align: center;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  &:hover {
    color: #fff;
  }
  &.active {
    border-left-color: ${BRIGHT_THEME_COLOR};
    color: ${BRIGHT_THEME_COLOR};
  }
  &[data-badge]::after {
    display: inline-flex;
    content: attr(data-badge);
    color: white;
    border-radius: 100px;
    min-width: 20px;
    height: 20px;
    justify-content: center;
    align-items: center;
    transform: translate(-25%, -100%);
    font-size: 13px;
    padding: 0 4px;
    background: ${ERROR_COLOR};
  }
  ${(p) =>
    p.accent &&
    css`
      border-radius: 100px;
      background: ${transparentize(0.4, THEME_COLOR_DARK)};
      color: #fff;
      margin: 10px;
      padding: 9px 7px;
      &:hover {
        background: ${transparentize(0.7, THEME_COLOR_DARK)};
        color: #fff;
        box-shadow: 0px 0px 0px 2px ${THEME_COLOR};
      }
      &:active {
        color: #fff;
        transform: scale(0.95);
        box-shadow: 0px 0px 0px 2px ${THEME_COLOR};
      }
    `}
  ${(p) =>
    p.lesserAccent &&
    css`
      color: ${BRIGHT_THEME_COLOR};
      &:hover {
        color: ${BRIGHT_THEME_COLOR};
        transform: scale(1.05);
      }
      &:active {
        color: ${BRIGHT_THEME_COLOR};
        transform: scale(0.98);
      }
    `}
`;

export const NavbarLinkMobile = styled(NavLink)`
  display: inline-block;
  color: #9598a6;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  padding: 10px 15px;
  transition-duration: 0.2s;
  letter-spacing: 0.5px;
  font-weight: 500;
  text-align: center;
  border-bottom: 3px solid transparent;
  position: relative;
  &:hover {
    color: #fff;
  }
  &.active {
    border-bottom-color: ${BRIGHT_THEME_COLOR};
    color: ${BRIGHT_THEME_COLOR};
  }
  &[data-badge]::after {
    display: inline-flex;
    content: attr(data-badge);
    color: white;
    border-radius: 100px;
    min-width: 20px;
    height: 20px;
    justify-content: center;
    align-items: center;
    font-size: 13px;
    padding: 0 4px;
    background: ${ERROR_COLOR};
    position: absolute;
    right: 0px;
    top: 0px;
  }
`;

export const Profile = styled.div`
  font-size: 0.8em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 160px;
  padding: 10px;
`;

export const ProfilePicture = styled.button`
  border-radius: 1000px;
  cursor: pointer;
  background-color: transparent;
  border: 0;
  position: relative;
  flex-shrink: 0;
  margin-top: 8px;
  &:active {
    transform: scale(0.95);
  }
  & > div:first-child {
    background-size: 100%;
    width: 38px;
    height: 38px;
    overflow: hidden;
    border-radius: 1000px;
    margin: 4px 6px;
  }
`;

const ProfileIcon = styled(AccountCircle)`
  color: #fff;
  width: 42px;
  height: 42px;
  margin: 0px 4px;
`;

const ProfileBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(20%, -50%);
  color: white;
  font-size: 10px;
  background: ${ERROR_COLOR};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  border-radius: 100px;
  overflow: hidden;
  z-index: 100;
`;
